/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {FocusKeyManager} from '@angular/cdk/a11y';
import {coerceBooleanProperty} from '@angular/cdk/coercion';
import {SelectionModel} from '@angular/cdk/collections';
import {startWith} from '@angular/cdk/rxjs';
import {
  AfterContentInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  QueryList,
  Renderer2,
  Self,
  ViewEncapsulation,
} from '@angular/core';
import {ControlValueAccessor, FormGroupDirective, NgControl, NgForm} from '@angular/forms';
import {
  BACKSPACE,
  DELETE,
  Directionality,
  LEFT_ARROW,
  RIGHT_ARROW,
  UP_ARROW
} from '@angular/material/core';
import {MdFormFieldControl} from '@angular/material/form-field';

import {Observable} from 'rxjs/Observable';
import {merge} from 'rxjs/observable/merge';
import {Subject} from 'rxjs/Subject';
import {Subscription} from 'rxjs/Subscription';

import {MdChip, MdChipEvent, MdChipSelectionChange} from './chip';
import {MdChipInput} from './chip-input';

// Increasing integer for generating unique ids for chip-list components.
let /** @type {?} */ nextUniqueId = 0;
/**
 * Change event object that is emitted when the chip list value has changed.
 */
export class MdChipListChange {
/**
 * @param {?} source
 * @param {?} value
 */
constructor(public source: MdChipList,
public value: any) { }
}

function MdChipListChange_tsickle_Closure_declarations() {
/** @type {?} */
MdChipListChange.prototype.source;
/** @type {?} */
MdChipListChange.prototype.value;
}

/**
 * A material design chips component (named ChipList for it's similarity to the List component).
 */
export class MdChipList implements MdFormFieldControl<any>, ControlValueAccessor,
    AfterContentInit, OnInit, OnDestroy {
/**
 * Stream that emits whenever the state of the input changes such that the wrapping `MdFormField`
 * needs to run change detection.
 */
stateChanges = new Subject<void>();
/**
 * When a chip is destroyed, we track the index so we can focus the appropriate next chip.
 */
protected _lastDestroyedIndex: number|null = null;
/**
 * Track which chips we're listening to for focus/destruction.
 */
protected _chipSet: WeakMap<MdChip, boolean> = new WeakMap();
/**
 * Subscription to tabbing out from the chip list.
 */
private _tabOutSubscription = Subscription.EMPTY;
/**
 * Subscription to changes in the chip list.
 */
private _changeSubscription: Subscription;
/**
 * Subscription to focus changes in the chips.
 */
private _chipFocusSubscription: Subscription|null;
/**
 * Subscription to blur changes in the chips.
 */
private _chipBlurSubscription: Subscription|null;
/**
 * Subscription to selection changes in chips.
 */
private _chipSelectionSubscription: Subscription|null;
/**
 * Subscription to remove changes in chips.
 */
private _chipRemoveSubscription: Subscription|null;
/**
 * Whether or not the chip is selectable.
 */
protected _selectable: boolean = true;
/**
 * Whether the component is in multiple selection mode.
 */
private _multiple: boolean = false;
/**
 * The chip input to add more chips
 */
protected _chipInput: MdChipInput;
/**
 * The aria-describedby attribute on the chip list for improved a11y.
 */
protected _ariaDescribedby: string;
/**
 * Id of the chip list
 */
protected _id: string;
/**
 * Uid of the chip list
 */
protected _uid: string = `md-chip-list-${nextUniqueId++}`;
/**
 * Whether this is required
 */
protected _required: boolean = false;
/**
 * Whether this is disabled
 */
protected _disabled: boolean = false;

  protected _value: any;
/**
 * Placeholder for the chip list. Alternatively, placeholder can be set on MdChipInput
 */
protected _placeholder: string;
/**
 * Tab index for the chip list.
 */
_tabIndex = 0;
/**
 * User defined tab index.
 * When it is not null, use user defined tab index. Otherwise use _tabIndex
 */
_userTabIndex: number | null = null;
/**
 * The FocusKeyManager which handles focus.
 */
_keyManager: FocusKeyManager<MdChip>;
/**
 * Function when touched
 */
_onTouched = () => {};
/**
 * Function when changed
 */
_onChange: (value: any) => void = () => {};

  _selectionModel: SelectionModel<MdChip>;
/**
 * Comparison function to specify which option is displayed. Defaults to object equality.
 */
private _compareWith = (o1: any, o2: any) => o1 === o2;
/**
 * The array of selected chips inside chip list.
 * @return {?}
 */
get selected(): MdChip[] | MdChip {
    return this.multiple ? this._selectionModel.selected : this._selectionModel.selected[0];
  }
/**
 * Whether the user should be allowed to select multiple chips.
 * @return {?}
 */
get multiple(): boolean { return this._multiple; }
/**
 * @param {?} value
 * @return {?}
 */
set multiple(value: boolean) {
    this._multiple = coerceBooleanProperty(value);
  }
/**
 * A function to compare the option values with the selected values. The first argument
 * is a value from an option. The second is a value from the selection. A boolean
 * should be returned.
 * @return {?}
 */
get compareWith() { return this._compareWith; }
/**
 * @param {?} fn
 * @return {?}
 */
set compareWith(fn: (o1: any, o2: any) => boolean) {
    this._compareWith = fn;
    if (this._selectionModel) {
      // A different comparator means the selection could change.
      this._initializeSelection();
    }
  }
/**
 * Required for FormFieldControl
 * @return {?}
 */
get value() { return this._value; }
/**
 * @param {?} newValue
 * @return {?}
 */
set value(newValue: any) {
    this.writeValue(newValue);
    this._value = newValue;
  }
/**
 * Required for FormFieldControl. The ID of the chip list
 * @param {?} value
 * @return {?}
 */
set id(value: string) {
    this._id = value;
    this.stateChanges.next();
  }
/**
 * @return {?}
 */
get id() { return this._id || this._uid; }
/**
 * Required for FormFieldControl. Whether the chip list is required.
 * @param {?} value
 * @return {?}
 */
set required(value: any) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
/**
 * @return {?}
 */
get required() {
    return this._required;
  }
/**
 * For FormFieldControl. Use chip input's placholder if there's a chip input
 * @param {?} value
 * @return {?}
 */
set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
/**
 * @return {?}
 */
get placeholder() {
    return this._chipInput ? this._chipInput.placeholder : this._placeholder;
  }
/**
 * Whether any chips or the mdChipInput inside of this chip-list has focus.
 * @return {?}
 */
get focused() {
    return this.chips.some(chip => chip._hasFocus) ||
      (this._chipInput && this._chipInput.focused);
  }
/**
 * Whether this chip-list contains no chips and no mdChipInput.
 * @return {?}
 */
get empty(): boolean {
    return (!this._chipInput || this._chipInput.empty) && this.chips.length === 0;
  }
/**
 * Whether this chip-list is disabled.
 * @return {?}
 */
get disabled() { return this.ngControl ? this.ngControl.disabled : this._disabled; }
/**
 * @param {?} value
 * @return {?}
 */
set disabled(value: any) { this._disabled = coerceBooleanProperty(value); }
/**
 * Whether the chip list is in an error state.
 * @return {?}
 */
get errorState(): boolean {
    const /** @type {?} */ isInvalid = this.ngControl && this.ngControl.invalid;
    const /** @type {?} */ isTouched = this.ngControl && this.ngControl.touched;
    const /** @type {?} */ isSubmitted = (this._parentFormGroup && this._parentFormGroup.submitted) ||
      (this._parentForm && this._parentForm.submitted);
    return !!(isInvalid && (isTouched || isSubmitted));
  }
/**
 * Orientation of the chip list.
 */
ariaOrientation: 'horizontal' | 'vertical' = 'horizontal';
/**
 * Whether or not this chip is selectable. When a chip is not selectable,
 * its selected state is always ignored.
 * @return {?}
 */
get selectable(): boolean { return this._selectable; }
/**
 * @param {?} value
 * @return {?}
 */
set selectable(value: boolean) { this._selectable = coerceBooleanProperty(value); }
/**
 * @param {?} value
 * @return {?}
 */
set tabIndex(value: number) {
    this._userTabIndex = value;
    this._tabIndex = value;
  }
/**
 * Combined stream of all of the child chips' selection change events.
 * @return {?}
 */
get chipSelectionChanges(): Observable<MdChipSelectionChange> {
    return merge(...this.chips.map(chip => chip.selectionChange));
  }
/**
 * Combined stream of all of the child chips' focus change events.
 * @return {?}
 */
get chipFocusChanges(): Observable<MdChipEvent> {
    return merge(...this.chips.map(chip => chip._onFocus));
  }
/**
 * Combined stream of all of the child chips' blur change events.
 * @return {?}
 */
get chipBlurChanges(): Observable<MdChipEvent> {
    return merge(...this.chips.map(chip => chip._onBlur));
  }
/**
 * Combined stream of all of the child chips' remove change events.
 * @return {?}
 */
get chipRemoveChanges(): Observable<MdChipEvent> {
    return merge(...this.chips.map(chip => chip.destroy));
  }
/**
 * Event emitted when the selected chip list value has been changed by the user.
 */
change: EventEmitter<MdChipListChange> = new EventEmitter<MdChipListChange>();
/**
 * Event that emits whenever the raw value of the chip-list changes. This is here primarily
 * to facilitate the two-way binding for the `value` input.
 * \@docs-private
 */
valueChange = new EventEmitter<any>();
/**
 * The chip components contained within this chip list.
 */
chips: QueryList<MdChip>;
/**
 * @param {?} _renderer
 * @param {?} _elementRef
 * @param {?} _changeDetectorRef
 * @param {?} _dir
 * @param {?} _parentForm
 * @param {?} _parentFormGroup
 * @param {?} ngControl
 */
constructor(protected _renderer: Renderer2,
              protected _elementRef: ElementRef,
private _changeDetectorRef: ChangeDetectorRef,
private _dir: Directionality,
private _parentForm: NgForm,
private _parentFormGroup: FormGroupDirective,
public ngControl: NgControl) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }
/**
 * @return {?}
 */
ngAfterContentInit(): void {

    this._keyManager = new FocusKeyManager<MdChip>(this.chips).withWrap();

    // Prevents the chip list from capturing focus and redirecting
    // it back to the first chip when the user tabs out.
    this._tabOutSubscription = this._keyManager.tabOut.subscribe(() => {
      this._tabIndex = -1;
      setTimeout(() => this._tabIndex = this._userTabIndex || 0);
    });

    // When the list changes, re-subscribe
    this._changeSubscription = startWith.call(this.chips.changes, null).subscribe(() => {
      this._resetChips();

      // Reset chips selected/deselected status
      this._initializeSelection();

      // Check to see if we need to update our tab index
      this._updateTabIndex();

      // Check to see if we have a destroyed chip and need to refocus
      this._updateFocusForDestroyedChips();
    });
  }
/**
 * @return {?}
 */
ngOnInit() {
    this._selectionModel = new SelectionModel<MdChip>(this.multiple, undefined, false);
    this.stateChanges.next();
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this._tabOutSubscription.unsubscribe();

    if (this._changeSubscription) {
      this._changeSubscription.unsubscribe();
    }
    this._dropSubscriptions();
  }
/**
 * Associates an HTML input element with this chip list.
 * @param {?} inputElement
 * @return {?}
 */
registerInput(inputElement: MdChipInput) {
    this._chipInput = inputElement;
  }
/**
 * @param {?} ids
 * @return {?}
 */
setDescribedByIds(ids: string[]) { this._ariaDescribedby = ids.join(' '); }
/**
 * @param {?} value
 * @return {?}
 */
writeValue(value: any): void {
    if (this.chips) {
      this._setSelectionByValue(value, false);
    }
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
/**
 * @param {?} disabled
 * @return {?}
 */
setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this._renderer.setProperty(this._elementRef.nativeElement, 'disabled', disabled);
    this.stateChanges.next();
  }
/**
 * Focuses the the first non-disabled chip in this chip list, or the associated input when there
 * are no eligible chips.
 * @return {?}
 */
focus() {
    // TODO: ARIA says this should focus the first `selected` chip if any are selected.
    // Focus on first element if there's no chipInput inside chip-list
    if (this._chipInput && this._chipInput.focused) {
      // do nothing
    } else if (this.chips.length > 0) {
      this._keyManager.setFirstItemActive();
      this.stateChanges.next();
    } else {
      this._focusInput();
      this.stateChanges.next();
    }
  }
/**
 * Attempt to focus an input if we have one.
 * @return {?}
 */
_focusInput() {
    if (this._chipInput) {
      this._chipInput.focus();
    }
  }
/**
 * Pass events to the keyboard manager. Available here for tests.
 * @param {?} event
 * @return {?}
 */
_keydown(event: KeyboardEvent) {
    let /** @type {?} */ code = event.keyCode;
    let /** @type {?} */ target = /** @type {?} */(( event.target as HTMLElement));
    let /** @type {?} */ isInputEmpty = this._isInputEmpty(target);
    let /** @type {?} */ isRtl = this._dir && this._dir.value == 'rtl';

    let /** @type {?} */ isPrevKey = (code === (isRtl ? RIGHT_ARROW : LEFT_ARROW));
    let /** @type {?} */ isNextKey = (code === (isRtl ? LEFT_ARROW : RIGHT_ARROW));
    let /** @type {?} */ isBackKey = (code === BACKSPACE || code == DELETE || code == UP_ARROW || isPrevKey);
    // If they are on an empty input and hit backspace/delete/left arrow, focus the last chip
    if (isInputEmpty && isBackKey) {
      this._keyManager.setLastItemActive();
      event.preventDefault();
      return;
    }

    // If they are on a chip, check for space/left/right, otherwise pass to our key manager (like
    // up/down keys)
    if (target && target.classList.contains('mat-chip')) {
      if (isPrevKey) {
        this._keyManager.setPreviousItemActive();
        event.preventDefault();
      } else if (isNextKey) {
        this._keyManager.setNextItemActive();
        event.preventDefault();
      } else {
        this._keyManager.onKeydown(event);
      }
    }
    this.stateChanges.next();
  }
/**
 * Check the tab index as you should not be allowed to focus an empty list.
 * @return {?}
 */
protected _updateTabIndex(): void {
    // If we have 0 chips, we should not allow keyboard focus
    this._tabIndex = this._userTabIndex || (this.chips.length === 0 ? -1 : 0);
  }
/**
 * Update key manager's active item when chip is deleted.
 * If the deleted chip is the last chip in chip list, focus the new last chip.
 * Otherwise focus the next chip in the list.
 * Save `_lastDestroyedIndex` so we can set the correct focus.
 * @param {?} chip
 * @return {?}
 */
protected _updateKeyManager(chip: MdChip) {
    let /** @type {?} */ chipIndex: number = this.chips.toArray().indexOf(chip);
    if (this._isValidIndex(chipIndex)) {
      if (chip._hasFocus) {
        // Check whether the chip is not the last item
        if (chipIndex < this.chips.length - 1) {
          this._keyManager.setActiveItem(chipIndex);
        } else if (chipIndex - 1 >= 0) {
          this._keyManager.setActiveItem(chipIndex - 1);
        }
      }
      if (this._keyManager.activeItemIndex === chipIndex) {
        this._lastDestroyedIndex = chipIndex;
      }
    }
  }
/**
 * Checks to see if a focus chip was recently destroyed so that we can refocus the next closest
 * one.
 * @return {?}
 */
protected _updateFocusForDestroyedChips() {
    let /** @type {?} */ chipsArray = this.chips;

    if (this._lastDestroyedIndex != null && chipsArray.length > 0) {
      // Check whether the destroyed chip was the last item
      const /** @type {?} */ newFocusIndex = Math.min(this._lastDestroyedIndex, chipsArray.length - 1);
      this._keyManager.setActiveItem(newFocusIndex);
      let /** @type {?} */ focusChip = this._keyManager.activeItem;
      // Focus the chip
      if (focusChip) {
        focusChip.focus();
      }
    } else if (chipsArray.length === 0) {
      this._focusInput();
    }

    // Reset our destroyed index
    this._lastDestroyedIndex = null;
  }
/**
 * Utility to ensure all indexes are valid.
 * 
 * @param {?} index The index to be checked.
 * @return {?} True if the index is valid for our list of chips.
 */
private _isValidIndex(index: number): boolean {
    return index >= 0 && index < this.chips.length;
  }
/**
 * @param {?} element
 * @return {?}
 */
private _isInputEmpty(element: HTMLElement): boolean {
    if (element && element.nodeName.toLowerCase() === 'input') {
      let /** @type {?} */ input = /** @type {?} */(( element as HTMLInputElement));

      return !input.value;
    }

    return false;
  }
/**
 * @param {?} value
 * @param {?=} isUserInput
 * @return {?}
 */
_setSelectionByValue(value: any, isUserInput: boolean = true) {
    this._clearSelection();
    this.chips.forEach(chip => chip.deselect());

    if (Array.isArray(value)) {
      value.forEach(currentValue => this._selectValue(currentValue, isUserInput));
      this._sortValues();
    } else {
      const /** @type {?} */ correspondingChip = this._selectValue(value, isUserInput);

      // Shift focus to the active item. Note that we shouldn't do this in multiple
      // mode, because we don't know what chip the user interacted with last.
      if (correspondingChip) {
        this._keyManager.setActiveItem(this.chips.toArray().indexOf(correspondingChip));
      }
    }
  }
/**
 * Finds and selects the chip based on its value.
 * @param {?} value
 * @param {?=} isUserInput
 * @return {?} Chip that has the corresponding value.
 */
private _selectValue(value: any, isUserInput: boolean = true): MdChip | undefined {

    const /** @type {?} */ correspondingChip = this.chips.find(chip => {
      return chip.value != null && this._compareWith(chip.value,  value);
    });

    if (correspondingChip) {
      isUserInput ? correspondingChip.selectViaInteraction() : correspondingChip.select();
      this._selectionModel.select(correspondingChip);
    }

    return correspondingChip;
  }
/**
 * @return {?}
 */
private _initializeSelection(): void {
    // Defer setting the value in order to avoid the "Expression
    // has changed after it was checked" errors from Angular.
    Promise.resolve().then(() => {
      this._setSelectionByValue(this.ngControl ? this.ngControl.value : this._value, false);
      this.stateChanges.next();
    });
  }
/**
 * Deselects every chip in the list.
 * @param {?=} skip Chip that should not be deselected.
 * @return {?}
 */
private _clearSelection(skip?: MdChip): void {
    this._selectionModel.clear();
    this.chips.forEach(chip => {
      if (chip !== skip) {
        chip.deselect();
      }
    });
    this.stateChanges.next();
  }
/**
 * Sorts the model values, ensuring that they keep the same
 * order that they have in the panel.
 * @return {?}
 */
private _sortValues(): void {
    if (this._multiple) {
      this._selectionModel.clear();

      this.chips.forEach(chip => {
        if (chip.selected) {
          this._selectionModel.select(chip);
        }
      });
      this.stateChanges.next();
    }
  }
/**
 * Emits change event to set the model value.
 * @param {?=} fallbackValue
 * @return {?}
 */
private _propagateChanges(fallbackValue?: any): void {
    let /** @type {?} */ valueToEmit: any = null;

    if (Array.isArray(this.selected)) {
      valueToEmit = this.selected.map(chip => chip.value);
    } else {
      valueToEmit = this.selected ? this.selected.value : fallbackValue;
    }
    this._value = valueToEmit;
    this.change.emit(new MdChipListChange(this, valueToEmit));
    this.valueChange.emit(valueToEmit);
    this._onChange(valueToEmit);
    this._changeDetectorRef.markForCheck();
  }
/**
 * When blurred, mark the field as touched when focus moved outside the chip list.
 * @return {?}
 */
_blur() {
    if (!this.disabled) {
      if (this._chipInput) {
        // If there's a chip input, we should check whether the focus moved to chip input.
        // If the focus is not moved to chip input, mark the field as touched. If the focus moved
        // to chip input, do nothing.
        // Timeout is needed to wait for the focus() event trigger on chip input.
        setTimeout(() => {
          if (!this.focused) {
            this._markAsTouched();
          }
        });
      } else {
        // If there's no chip input, then mark the field as touched.
        this._markAsTouched();
      }
    }
  }
/**
 * Mark the field as touched
 * @return {?}
 */
_markAsTouched() {
    this._onTouched();
    this._changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }
/**
 * @return {?}
 */
private _resetChips() {
    this._dropSubscriptions();
    this._listenToChipsFocus();
    this._listenToChipsSelection();
    this._listenToChipsRemoved();
  }
/**
 * @return {?}
 */
private _dropSubscriptions() {
    if (this._chipFocusSubscription) {
      this._chipFocusSubscription.unsubscribe();
      this._chipFocusSubscription = null;
    }

    if (this._chipBlurSubscription) {
      this._chipBlurSubscription.unsubscribe();
      this._chipBlurSubscription = null;
    }

    if (this._chipSelectionSubscription) {
      this._chipSelectionSubscription.unsubscribe();
      this._chipSelectionSubscription = null;
    }
  }
/**
 * Listens to user-generated selection events on each chip.
 * @return {?}
 */
private _listenToChipsSelection(): void {
    this._chipSelectionSubscription = this.chipSelectionChanges.subscribe(event => {
      event.source.selected
        ? this._selectionModel.select(event.source)
        : this._selectionModel.deselect(event.source);

      // For single selection chip list, make sure the deselected value is unselected.
      if (!this.multiple) {
        this.chips.forEach(chip => {
          if (!this._selectionModel.isSelected(chip) && chip.selected) {
            chip.deselect();
          }
        });
      }

      if (event.isUserInput) {
        this._propagateChanges();
      }
    });
  }
/**
 * Listens to user-generated selection events on each chip.
 * @return {?}
 */
private _listenToChipsFocus(): void {
    this._chipFocusSubscription = this.chipFocusChanges.subscribe(event => {
      let /** @type {?} */ chipIndex: number = this.chips.toArray().indexOf(event.chip);

      if (this._isValidIndex(chipIndex)) {
        this._keyManager.updateActiveItemIndex(chipIndex);
      }
      this.stateChanges.next();
    });

    this._chipBlurSubscription = this.chipBlurChanges.subscribe(_ => {
      this._blur();
      this.stateChanges.next();
    });
  }
/**
 * @return {?}
 */
private _listenToChipsRemoved(): void {
    this._chipRemoveSubscription = this.chipRemoveChanges.subscribe((event) => {
      this._updateKeyManager(event.chip);
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-chip-list, mat-chip-list',
  template: `<div class="mat-chip-list-wrapper"><ng-content></ng-content></div>`,
  exportAs: 'mdChipList, matChipList',
  host: {
    '[attr.tabindex]': '_tabIndex',
    '[attr.aria-describedby]': '_ariaDescribedby || null',
    '[attr.aria-required]': 'required.toString()',
    '[attr.aria-disabled]': 'disabled.toString()',
    '[attr.aria-invalid]': 'errorState',
    '[attr.aria-multiselectable]': 'multiple',
    '[class.mat-chip-list-disabled]': 'disabled',
    '[class.mat-chip-list-invalid]': 'errorState',
    '[class.mat-chip-list-required]': 'required',
    'role': 'listbox',
    '[attr.aria-orientation]': 'ariaOrientation',
    'class': 'mat-chip-list',
    '(focus)': 'focus()',
    '(blur)': '_blur()',
    '(keydown)': '_keydown($event)'
  },
  providers: [{provide: MdFormFieldControl, useExisting: MdChipList}],
  styleUrls: ['chips.css'],
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  changeDetection: ChangeDetectionStrategy.OnPush
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Renderer2, },
{type: ElementRef, },
{type: ChangeDetectorRef, },
{type: Directionality, decorators: [{ type: Optional }, ]},
{type: NgForm, decorators: [{ type: Optional }, ]},
{type: FormGroupDirective, decorators: [{ type: Optional }, ]},
{type: NgControl, decorators: [{ type: Optional }, { type: Self }, ]},
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'multiple': [{ type: Input },],
'compareWith': [{ type: Input },],
'value': [{ type: Input },],
'id': [{ type: Input },],
'required': [{ type: Input },],
'placeholder': [{ type: Input },],
'disabled': [{ type: Input },],
'ariaOrientation': [{ type: Input, args: ['aria-orientation', ] },],
'selectable': [{ type: Input },],
'tabIndex': [{ type: Input },],
'change': [{ type: Output },],
'valueChange': [{ type: Output },],
'chips': [{ type: ContentChildren, args: [MdChip, ] },],
};
}

function MdChipList_tsickle_Closure_declarations() {
/** @type {?} */
MdChipList.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdChipList.ctorParameters;
/** @type {?} */
MdChipList.propDecorators;
/**
 * Stream that emits whenever the state of the input changes such that the wrapping `MdFormField`
 * needs to run change detection.
 * @type {?}
 */
MdChipList.prototype.stateChanges;
/**
 * When a chip is destroyed, we track the index so we can focus the appropriate next chip.
 * @type {?}
 */
MdChipList.prototype._lastDestroyedIndex;
/**
 * Track which chips we're listening to for focus/destruction.
 * @type {?}
 */
MdChipList.prototype._chipSet;
/**
 * Subscription to tabbing out from the chip list.
 * @type {?}
 */
MdChipList.prototype._tabOutSubscription;
/**
 * Subscription to changes in the chip list.
 * @type {?}
 */
MdChipList.prototype._changeSubscription;
/**
 * Subscription to focus changes in the chips.
 * @type {?}
 */
MdChipList.prototype._chipFocusSubscription;
/**
 * Subscription to blur changes in the chips.
 * @type {?}
 */
MdChipList.prototype._chipBlurSubscription;
/**
 * Subscription to selection changes in chips.
 * @type {?}
 */
MdChipList.prototype._chipSelectionSubscription;
/**
 * Subscription to remove changes in chips.
 * @type {?}
 */
MdChipList.prototype._chipRemoveSubscription;
/**
 * Whether or not the chip is selectable.
 * @type {?}
 */
MdChipList.prototype._selectable;
/**
 * Whether the component is in multiple selection mode.
 * @type {?}
 */
MdChipList.prototype._multiple;
/**
 * The chip input to add more chips
 * @type {?}
 */
MdChipList.prototype._chipInput;
/**
 * The aria-describedby attribute on the chip list for improved a11y.
 * @type {?}
 */
MdChipList.prototype._ariaDescribedby;
/**
 * Id of the chip list
 * @type {?}
 */
MdChipList.prototype._id;
/**
 * Uid of the chip list
 * @type {?}
 */
MdChipList.prototype._uid;
/**
 * Whether this is required
 * @type {?}
 */
MdChipList.prototype._required;
/**
 * Whether this is disabled
 * @type {?}
 */
MdChipList.prototype._disabled;
/** @type {?} */
MdChipList.prototype._value;
/**
 * Placeholder for the chip list. Alternatively, placeholder can be set on MdChipInput
 * @type {?}
 */
MdChipList.prototype._placeholder;
/**
 * Tab index for the chip list.
 * @type {?}
 */
MdChipList.prototype._tabIndex;
/**
 * User defined tab index.
 * When it is not null, use user defined tab index. Otherwise use _tabIndex
 * @type {?}
 */
MdChipList.prototype._userTabIndex;
/**
 * The FocusKeyManager which handles focus.
 * @type {?}
 */
MdChipList.prototype._keyManager;
/**
 * Function when touched
 * @type {?}
 */
MdChipList.prototype._onTouched;
/**
 * Function when changed
 * @type {?}
 */
MdChipList.prototype._onChange;
/** @type {?} */
MdChipList.prototype._selectionModel;
/**
 * Comparison function to specify which option is displayed. Defaults to object equality.
 * @type {?}
 */
MdChipList.prototype._compareWith;
/**
 * Orientation of the chip list.
 * @type {?}
 */
MdChipList.prototype.ariaOrientation;
/**
 * Event emitted when the selected chip list value has been changed by the user.
 * @type {?}
 */
MdChipList.prototype.change;
/**
 * Event that emits whenever the raw value of the chip-list changes. This is here primarily
 * to facilitate the two-way binding for the `value` input.
 * \@docs-private
 * @type {?}
 */
MdChipList.prototype.valueChange;
/**
 * The chip components contained within this chip list.
 * @type {?}
 */
MdChipList.prototype.chips;
/** @type {?} */
MdChipList.prototype._renderer;
/** @type {?} */
MdChipList.prototype._elementRef;
/** @type {?} */
MdChipList.prototype._changeDetectorRef;
/** @type {?} */
MdChipList.prototype._dir;
/** @type {?} */
MdChipList.prototype._parentForm;
/** @type {?} */
MdChipList.prototype._parentFormGroup;
/** @type {?} */
MdChipList.prototype.ngControl;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
